import { useMoneyFormatter } from "@/lib/money-format";
/**
 * /companies/:companyId/reportes/retenciones
 * Reporte de Retenciones view.
 */
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Percent, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { WithholdingsReportView } from "@/components/reports/WithholdingsReportView";
import { PrintableReport } from "@/components/reports/PrintableReport";
import { ReportPeriodPicker, type PeriodRange } from "@/components/reports/ReportPeriodPicker";
import { CsvExportButton } from "@/components/reports/CsvExportButton";
import { useActiveCompany } from "@/lib/company-context";
import { useWithholdingsReport } from "@/lib/api/reports.queries";

export const Route = createFileRoute("/companies/$companyId/reportes/retenciones")({
  component: RetencionesPage,
});

function currentMonthRange(): PeriodRange {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(lastDay).padStart(2, "0")}` };
}

function RetencionesPage() {
  return (
    <RequireAuth>
      <AppShell>
        <RetencionesContent />
      </AppShell>
    </RequireAuth>
  );
}

function RetencionesContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const [period, setPeriod] = useState<PeriodRange>(currentMonthRange);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const { data: report, isLoading, isError, refetch } = useWithholdingsReport(companyId, {
    periodFrom: period.from,
    periodTo: period.to,
  });

  const formatMoney = useMoneyFormatter("decimal");

  const csvRows = report
    ? [
        ...report.retefuente.items.map((i) => ({
          tipo: "ReteFuente",
          tercero: i.terceroName,
          base: formatMoney(i.totalBase),
          retencion: formatMoney(i.totalRetained),
          documentos: i.documentCount,
        })),
        ...report.reteiva.items.map((i) => ({
          tipo: "ReteIVA",
          tercero: i.terceroName,
          base: formatMoney(i.totalBase),
          retencion: formatMoney(i.totalRetained),
          documentos: i.documentCount,
        })),
        ...report.reteica.items.map((i) => ({
          tipo: "ReteICA",
          tercero: i.terceroName,
          base: formatMoney(i.totalBase),
          retencion: formatMoney(i.totalRetained),
          documentos: i.documentCount,
        })),
      ]
    : [];

  return (
    <PrintableReport
      title={t("reports.withholdings.title")}
      subtitle={report ? `${report.period.from} — ${report.period.to}` : undefined}
    >
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-purple-600" />
            <h1 className="text-xl font-bold">{t("reports.withholdings.title")}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <CsvExportButton
              filename={`retenciones-${period.from}-${period.to}`}
              rows={csvRows}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="print:hidden"
              onClick={() => window.print()}
            >
              <Printer className="mr-2 h-4 w-4" />
              {t("reports.print")}
            </Button>
          </div>
        </div>

        <div className="print:hidden">
          <ReportPeriodPicker value={period} onChange={setPeriod} />
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        ) : isError ? (
          <ErrorState
            title={t("reports.error.title")}
            description={t("reports.error.body")}
            onRetry={() => void refetch()}
          />
        ) : report ? (
          <WithholdingsReportView report={report} />
        ) : null}
      </div>
    </PrintableReport>
  );
}
